'use client'
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Box } from '@factorialco/deprecated-design-system'
import { DeviceImageShowcase } from 'modules/it_management/components/DeviceImageCarousel/DeviceImageShowcase'
import { F0TagRaw } from '@factorialco/f0-react'
import { EyeVisible } from '@factorialco/f0-react/icons/app'

export interface DeviceImageCarouselProps {
  images: string[]
}

export const DeviceImageCarousel: React.FC<DeviceImageCarouselProps> = ({ images }) => {
  const [active, setActive] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  if (!images?.length) return null

  const visibleThumbs = images.slice(0, 3)
  const remaining = images.length - 3

  return (
    <>
      <Box className="w-full flex flex-col lg:flex-row" gap="s12">
        {images.length > 1 && (
          <Box className="w-full lg:w-1/4 flex flex-row lg:flex-col" gap="s12">
            {visibleThumbs.map((src, index) => {
              const isLastVisible = index === 2 && images.length > 3

              if (isLastVisible) {
                return (
                  <MoreThumbnail
                    key={`more-${index}`}
                    src={src}
                    remaining={remaining}
                    onClick={() => {
                      setActive(index)
                      setIsOpen(true)
                    }}
                  />
                )
              }

              return (
                <Thumbnail
                  key={`${src}-${index}`}
                  src={src}
                  isActive={index === active}
                  onClick={() => setActive(index)}
                />
              )
            })}
          </Box>
        )}

        <Box
          className={`w-full ${images.length > 1 ? 'lg:w-3/4' : ''}`}
          border={{
            all: { style: 'solid', width: 's1', color: 'grey200' },
          }}
          flexDirection="row"
          alignItems="center"
          borderRadius={{ all: 'abs008' }}
          onClick={() => setIsOpen(true)}
        >
          <AnimatePresence mode="wait">
            <motion.img
              className="w-full object-contain aspect-video"
              style={{ maxHeight: '300px' }}
              key={images[active]}
              src={images[active]}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.2 }}
            />
          </AnimatePresence>
        </Box>

        {isOpen && (
          <DeviceImageShowcase
            activeIndex={active}
            images={images}
            onClose={() => setIsOpen(false)}
          />
        )}
      </Box>
    </>
  )
}

function Thumbnail({
  src,
  isActive,
  onClick,
}: {
  src: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <Box
      className="w-full flex-1 flex justify-center cursor-pointer"
      border={{
        all: { style: 'solid', width: 's1', color: isActive ? 'grey600' : 'grey200' },
      }}
      borderRadius={{ all: 'abs008' }}
      onClick={onClick}
    >
      <img src={src} className="p-1 w-full object-contain aspect-video" />
    </Box>
  )
}

function MoreThumbnail({
  src,
  remaining,
  onClick,
}: {
  src: string
  remaining: number
  onClick: () => void
}) {
  return (
    <Box
      border={{
        all: { style: 'solid', width: 's1', color: 'grey200' },
      }}
      borderRadius={{ all: 'abs008' }}
      className="relative w-full flex-1 cursor-pointer"
      onClick={onClick}
    >
      <img
        src={src}
        className="p-1 w-full object-contain aspect-video"
        style={{ filter: 'blur(3px)' }}
      />
      <Box className="absolute inset-0 flex items-center justify-center drop-shadow-lg">
        <Box background="neutralBackground" borderRadius={{ all: 'abs100' }}>
          <F0TagRaw icon={EyeVisible} text={`+${remaining} more`} />
        </Box>
      </Box>
    </Box>
  )
}
